import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { useLiveData } from '../../hooks/useLiveData';
import { mergeEmrSettings } from '../../config/emrSettings';
import GetStartedChecklist from '../../components/onboarding/GetStartedChecklist';
import { useHospitalDirectory } from '../../hooks/useHospitalDirectory';

function QrPreview({ url, label }) {
  if (!url) return <p className="hint">Set a booking URL below to generate QR.</p>;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  return (
    <div style={{ textAlign: 'center' }}>
      <img src={qrSrc} alt={`QR for ${label}`} width={200} height={200} style={{ border: '1px solid #e2e8f0', borderRadius: 8 }} />
      <p className="hint" style={{ fontSize: 11, wordBreak: 'break-all', marginTop: 8 }}>{url}</p>
    </div>
  );
}

export default function ClinicGrowthPage() {
  const { user } = useAuth();
  const { hospital, reload: reloadProfile } = useHospitalProfile();
  const { data: dir } = useHospitalDirectory();
  const { data: settingsData, reload: reloadSettings } = useLiveData(() => ipApi.getEmrSettings(), []);
  const settings = mergeEmrSettings(settingsData?.settings);

  const defaultBooking = typeof window !== 'undefined'
    ? `${window.location.origin}/reception/queue`
    : 'https://networks.hcen.in/reception/queue';

  const [form, setForm] = useState({
    googleReviewUrl: '',
    clinicBookingUrl: defaultBooking,
  });
  const [announcement, setAnnouncement] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (hospital) {
      setForm({
        googleReviewUrl: hospital.googleReviewUrl || '',
        clinicBookingUrl: hospital.clinicBookingUrl || defaultBooking,
      });
    }
  }, [hospital, defaultBooking]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await ipApi.patchHospitalProfile({
        googleReviewUrl: form.googleReviewUrl.trim(),
        clinicBookingUrl: form.clinicBookingUrl.trim(),
      });
      await reloadProfile();
      setMsg('Clinic links saved.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const postAnnouncement = async () => {
    if (!announcement.trim()) return;
    setBusy(true);
    try {
      const list = [
        { id: `ANN-${Date.now()}`, text: announcement.trim(), at: new Date().toISOString(), by: user?.name || 'Manager' },
        ...(settings.pushAnnouncements || []),
      ].slice(0, 20);
      await ipApi.patchEmrSettings({ pushAnnouncements: list });
      setAnnouncement('');
      await reloadSettings();
      setMsg('Announcement posted to all staff dashboards.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendReviewSms = async () => {
    if (!form.googleReviewUrl) {
      setMsg('Add Google review link first.');
      return;
    }
    setMsg('Use SMS Center to send review link template to completed patients.');
  };

  return (
    <PageShell
      title="Clinic Growth"
      subtitle="QR booking, Google reviews, and staff announcements for your clinic."
    >
      {msg && <div className="hint mb" style={{ color: 'var(--color-primary-dark)' }}>{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 16 }}>
        <GetStartedChecklist hospital={hospital} directory={dir} emrSettings={settings} />
        <div className="card">
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Push updates to staff</h3>
          <p className="hint" style={{ fontSize: 12 }}>Broadcast to all staff on login dashboard.</p>
          <textarea className="input" rows={3} value={announcement} onChange={(e) => setAnnouncement(e.target.value)} placeholder="e.g. New lab timings from Monday…" />
          <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={postAnnouncement}>Post update</button>
          {(settings.pushAnnouncements || []).slice(0, 5).map((a) => (
            <div key={a.id} className="hint" style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 6, fontSize: 12 }}>
              <strong>{a.by}</strong> · {new Date(a.at).toLocaleString('en-IN')}<br />{a.text}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <div className="card">
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Clinic QR — ABDM & appointments</h3>
          <QrPreview url={form.clinicBookingUrl} label="booking" />
          <form onSubmit={saveProfile} style={{ marginTop: 12 }}>
            <div className="field full">
              <label>Self check-in / booking URL</label>
              <input className="input" value={form.clinicBookingUrl} onChange={(e) => setForm({ ...form, clinicBookingUrl: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-secondary btn-sm" disabled={busy}>Save QR link</button>
          </form>
          <p className="hint" style={{ fontSize: 11, marginTop: 8 }}>Print this QR at reception for walk-in scan-to-queue. ABDM HIP linking uses ABHA module when sandbox is live.</p>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Google review link</h3>
          <form onSubmit={saveProfile}>
            <div className="field full">
              <label>Google Maps review URL</label>
              <input className="input" value={form.googleReviewUrl} onChange={(e) => setForm({ ...form, googleReviewUrl: e.target.value })} placeholder="https://g.page/r/…/review" />
            </div>
            <div className="row-actions" style={{ marginTop: 8 }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>Save</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={sendReviewSms}>SMS template tip</button>
            </div>
          </form>
          <p className="hint" style={{ fontSize: 11, marginTop: 12 }}>
            After consultation, reception can SMS: &quot;Thank you for visiting {hospital?.name || user?.hospital}. Please rate us: {form.googleReviewUrl || '[link]'}&quot;
          </p>
        </div>
      </div>
    </PageShell>
  );
}
